import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, FileText, Link2, MoreHorizontal, ShieldCheck, Sparkles, UploadCloud, Upload } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import CreateKBModal from "@/pages/knowledge/components/CreateKBModal";
import type { KnowledgeBase } from "@/mocks/knowledge";
import { loadKnowledgeBases, saveKnowledgeBases } from "@/pages/knowledge/store";
import type { KnowledgeItem } from "@/mocks/knowledge";
import { addKnowledgeItems } from "@/pages/knowledge/itemStore";

const importMethods = [
  { id: "doi", label: "DOI导入", desc: "输入 DOI 号，自动获取文献元数据与全文", color: "text-muted-foreground", bg: "bg-muted/35", icon: Link2 },
  { id: "ris", label: "RIS导入", desc: "上传 RIS 文件，批量导入参考文献", color: "text-muted-foreground", bg: "bg-muted/35", icon: FileText },
  { id: "bibtex", label: "BibTeX导入", desc: "上传 BibTeX 文件，批量导入参考文献", color: "text-muted-foreground", bg: "bg-muted/35", icon: FileText },
  { id: "arxiv", label: "arXiv导入", desc: "输入 arXiv 链接或 ID，快速导入预印本文献", color: "text-muted-foreground", bg: "bg-muted/35", icon: BookOpen },
  { id: "chat", label: "会话导入", desc: "兼容分享过来的对话，直接沉淀到知识库", color: "text-muted-foreground", bg: "bg-muted/35", icon: Sparkles },
  { id: "social", label: "社媒链接", desc: "导入网页链接、博客、新闻等内容", color: "text-muted-foreground", bg: "bg-muted/35", icon: Link2 },
  { id: "result", label: "成果入库", desc: "将报告、研究成果等内容入库管理", color: "text-muted-foreground", bg: "bg-muted/35", icon: UploadCloud },
];

const importTasks = [
  { name: "高通量材料热涨稳定性（V1.2）.pdf", type: "PDF", size: "12.4 MB · 38页", status: "parsing", progress: 60, time: "今天 14:32" },
  { name: "拓扑光子学关键论文（32篇）.docx", type: "DOCX", size: "2.1 MB", status: "done", time: "今天 11:08" },
  { name: "10.1038/s41586-021-03821-3", type: "DOI", size: "DOI导入", status: "done", time: "昨天 17:21" },
  { name: "references_2024_05.ris", type: "RIS", size: "RIS · 254条记录", status: "failed", time: "昨天 09:34" },
];

const analysisSettings = [
  { label: "自动提取元数据", desc: "从文献中自动提取标题、作者、期刊、关键词等信息", enabled: true },
  { label: "生成 AI 导读", desc: "由 AI 生成内容摘要与导读，便于快速理解", enabled: true },
  { label: "加入知识集", desc: "导入后加入指定知识集，便于分类管理", enabled: false, selectLabel: "请选择知识集" },
  { label: "开启索引图谱", desc: "构建知识图谱索引，关联相关文献与概念", enabled: true },
];

type ImportMethodId = (typeof importMethods)[number]["id"] | "file";
type TaskStatus = "queued" | "parsing" | "done" | "failed";
type ImportTask = {
  id: string;
  method: ImportMethodId;
  name: string;
  type: string;
  size: string;
  status: TaskStatus;
  progress?: number;
  time: string;
  kbId: string;
  payload?: {
    ref?: string;
    fileName?: string;
    // arXiv
    arxivId?: string;
    absUrl?: string;
    pdfUrl?: string;
    title?: string;
    abstract?: string;
    authors?: string[];
    published?: string; // yyyy-mm-dd
  };
  createdItemIds?: string[];
};

function fmtNow() {
  const s = new Date().toLocaleString("zh-CN");
  return s.replace(/\//g, "-").slice(0, 16);
}

function humanSize(bytes: number) {
  if (!Number.isFinite(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${mb.toFixed(1)} MB`;
}

function extType(name: string) {
  const ext = name.toLowerCase().split(".").pop() || "";
  if (ext === "pdf") return { sourceType: "pdf" as const, label: "PDF" };
  if (ext === "docx" || ext === "doc") return { sourceType: "docx" as const, label: ext.toUpperCase() };
  if (ext === "ris") return { sourceType: "ris" as const, label: "RIS" };
  if (ext === "bib" || ext === "bibtex") return { sourceType: "bibtex" as const, label: "BibTeX" };
  if (["png", "jpg", "jpeg", "webp"].includes(ext)) return { sourceType: "social" as const, label: "图片" };
  if (["md", "markdown", "txt"].includes(ext)) return { sourceType: "docx" as const, label: "文档" };
  return { sourceType: "docx" as const, label: ext ? ext.toUpperCase() : "文件" };
}

function parseArxivId(input: string): string | null {
  const v = input.trim();
  if (!v) return null;
  const m1 = v.match(/arxiv\.org\/abs\/([^?#]+)/i);
  if (m1?.[1]) return m1[1].replace(/\.pdf$/i, "");
  const m2 = v.match(/arxiv\.org\/pdf\/([^?#]+)/i);
  if (m2?.[1]) return m2[1].replace(/\.pdf$/i, "");
  if (/^\d{4}\.\d{4,5}(v\d+)?$/i.test(v)) return v;
  if (/^[a-z-]+\/\d{7}(v\d+)?$/i.test(v)) return v;
  return null;
}

async function tryFetchArxivMeta(arxivId: string) {
  try {
    const url = `https://export.arxiv.org/api/query?id_list=${encodeURIComponent(arxivId)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const xml = await res.text();
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    const entry = doc.querySelector("entry");
    if (!entry) return null;
    const title = entry.querySelector("title")?.textContent?.replace(/\s+/g, " ").trim() || "";
    const abstract = entry.querySelector("summary")?.textContent?.replace(/\s+/g, " ").trim() || "";
    const authors = Array.from(entry.querySelectorAll("author > name"))
      .map((n) => n.textContent?.trim())
      .filter(Boolean) as string[];
    const published = entry.querySelector("published")?.textContent?.slice(0, 10) || "";
    return { title, abstract, authors, published };
  } catch {
    return null;
  }
}

export default function ImportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [settings, setSettings] = useState(analysisSettings.map((s) => ({ ...s })));
  const [showCreate, setShowCreate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bases = useMemo(() => loadKnowledgeBases(), []);
  const initialKbId = searchParams.get("kbId") || bases[0]?.id || "kb1";
  const initialMethod = (searchParams.get("method") as ImportMethodId | null) || null;
  const [targetKbId, setTargetKbId] = useState<string>(initialKbId);
  const [tasks, setTasks] = useState<ImportTask[]>(() => [
    { id: "t1", method: "file", name: "高通量材料热涨稳定性（V1.2）.pdf", type: "PDF", size: "12.4 MB · 38页", status: "parsing", progress: 60, time: "今天 14:32", kbId: initialKbId },
    { id: "t2", method: "file", name: "拓扑光子学关键论文（32篇）.docx", type: "DOCX", size: "2.1 MB", status: "done", time: "今天 11:08", kbId: initialKbId },
    { id: "t3", method: "doi", name: "10.1038/s41586-021-03821-3", type: "DOI", size: "DOI导入", status: "done", time: "昨天 17:21", kbId: initialKbId },
    { id: "t4", method: "ris", name: "references_2024_05.ris", type: "RIS", size: "RIS · 254条记录", status: "failed", time: "昨天 09:34", kbId: initialKbId },
  ]);
  const [activeMethod, setActiveMethod] = useState<null | ImportMethodId>(initialMethod);
  const [methodInput, setMethodInput] = useState("");
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodError, setMethodError] = useState<string>("");
  const finalizedTaskIdsRef = useRef<Set<string>>(new Set());

  const handleCreate = (data: { name: string; description: string }) => {
    const newKb: KnowledgeBase = {
      id: `kb${Date.now()}`,
      name: data.name,
      description: data.description || "暂无描述",
      coverImage:
        "https://readdy.ai/api/search-image?query=abstract%20minimalist%20scientific%20research%20knowledge%20clean%20gradient%20calm%20professional%20soft%20background%20neutral&width=600&height=340&seq=kbdefault&orientation=landscape",
      tags: [],
      updatedAt: new Date().toISOString().replace("T", " ").slice(0, 16),
      size: "0KB",
      fileCount: 0,
      memberCount: 1,
    };
    const prev = loadKnowledgeBases();
    const next = [newKb, ...prev];
    saveKnowledgeBases(next);
    navigate(`/knowledge/${newKb.id}`);
  };

  const joinEnabled = settings.find((s) => s.label === "加入知识集")?.enabled ?? true;
  useEffect(() => {
    if (!joinEnabled) return;
    // keep targetKbId valid
    const exists = bases.some((b) => b.id === targetKbId);
    if (!exists && bases[0]?.id) setTargetKbId(bases[0].id);
  }, [bases, joinEnabled, targetKbId]);

  const startTaskProgress = (taskId: string) => {
    let p = 0;
    const timer = window.setInterval(() => {
      p += 12 + Math.round(Math.random() * 8);
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== taskId) return t;
          const nextP = Math.min(98, Math.max(t.progress ?? 0, p));
          return { ...t, status: "parsing", progress: nextP };
        }),
      );
    }, 450);
    window.setTimeout(() => {
      window.clearInterval(timer);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: "done", progress: 100 } : t)),
      );
      // create items when done
      const t = tasks.find((x) => x.id === taskId);
      if (!t) return;
    }, 2600 + Math.round(Math.random() * 1200));
  };

  const makeItemFromTask = (t: ImportTask): KnowledgeItem[] => {
    const now = fmtNow();
    const idBase = `it_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
    if (t.method === "doi") {
      const ref = t.payload?.ref || t.name;
      return [
        {
          id: idBase,
          kbId: t.kbId,
          sourceType: "doi",
          category: "literature",
          title: `DOI：${ref}`,
          description: "通过 DOI 导入的文献条目（已保存元数据）。",
          tags: ["DOI导入"],
          updatedAt: now,
          verified: true,
        },
      ];
    }
    if (t.method === "arxiv") {
      const arxivId = t.payload?.arxivId || parseArxivId(t.payload?.ref || t.name) || (t.payload?.ref || t.name);
      const absUrl = t.payload?.absUrl || (arxivId ? `https://arxiv.org/abs/${arxivId}` : undefined);
      const pdfUrl = t.payload?.pdfUrl || (arxivId ? `https://arxiv.org/pdf/${arxivId}.pdf` : undefined);
      const title = (t.payload?.title || "").trim();
      const shownTitle = title ? title : `arXiv：${arxivId}`;
      return [
        {
          id: idBase,
          kbId: t.kbId,
          sourceType: "arxiv",
          category: "literature",
          title: shownTitle,
          description: t.payload?.abstract ? t.payload.abstract.slice(0, 120) : "通过 arXiv 导入的预印本文献条目。",
          tags: ["arXiv导入"],
          updatedAt: now,
          verified: false,
          arxivId: arxivId || undefined,
          absUrl,
          pdfUrl,
          abstract: t.payload?.abstract || undefined,
          authors: t.payload?.authors || undefined,
          year: t.payload?.published?.slice(0, 4) || undefined,
        },
      ];
    }
    if (t.method === "social") {
      const ref = t.payload?.ref || t.name;
      return [
        {
          id: idBase,
          kbId: t.kbId,
          sourceType: "social",
          category: "document",
          title: `链接：${ref}`,
          description: "通过链接导入的网页内容（已创建待解析任务）。",
          tags: ["链接导入"],
          updatedAt: now,
          verified: false,
        },
      ];
    }
    if (t.method === "chat") {
      return [
        {
          id: idBase,
          kbId: t.kbId,
          sourceType: "chat",
          category: "chat",
          title: t.name,
          description: "导入的会话内容已沉淀为知识条目。",
          tags: ["会话导入"],
          updatedAt: now,
          verified: false,
        },
      ];
    }
    if (t.method === "result") {
      return [
        {
          id: idBase,
          kbId: t.kbId,
          sourceType: "result",
          category: "result",
          title: t.name,
          description: "成果已入库，可在知识库内容中检索与关联。",
          tags: ["成果入库"],
          updatedAt: now,
          verified: true,
        },
      ];
    }

    // file-like
    const guessed = extType(t.payload?.fileName || t.name);
    const src = guessed.sourceType;
    const cat: KnowledgeItem["category"] =
      src === "pdf" || src === "doi" || src === "ris" || src === "arxiv" ? "literature" : src === "chat" ? "chat" : src === "result" ? "result" : "document";
    return [
      {
        id: idBase,
        kbId: t.kbId,
        sourceType: src,
        category: cat,
        title: t.name,
        description: "通过文件导入的内容条目（已完成解析与入库）。",
        tags: ["文件导入"],
        updatedAt: now,
        size: t.size,
        verified: src === "pdf" || src === "docx",
      },
    ];
  };

  const finalizeTask = (taskId: string) => {
    setTasks((prev) => {
      const t = prev.find((x) => x.id === taskId);
      if (!t) return prev;
      if (finalizedTaskIdsRef.current.has(taskId)) {
        return prev.map((x) => (x.id === taskId ? { ...x, status: "done", progress: 100 } : x));
      }
      finalizedTaskIdsRef.current.add(taskId);
      const items = makeItemFromTask(t);
      // ensure idempotent: avoid double insert in dev/runtime edge cases
      addKnowledgeItems(items);
      return prev.map((x) => (x.id === taskId ? { ...x, status: "done", progress: 100, createdItemIds: items.map((i) => i.id) } : x));
    });
  };

  const queueTask = (partial: Omit<ImportTask, "id" | "time" | "status" | "progress" | "kbId"> & { kbId?: string }) => {
    const id = `task_${Date.now()}_${Math.random().toString(16).slice(2, 7)}`;
    const kbId = joinEnabled ? (partial.kbId || targetKbId) : (partial.kbId || targetKbId);
    const task: ImportTask = { ...partial, id, time: fmtNow(), status: "queued", progress: 0, kbId } as ImportTask;
    setTasks((prev) => [task, ...prev]);
    window.setTimeout(() => {
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status: "parsing", progress: 10 } : t)));
      startTaskProgress(id);
      window.setTimeout(() => finalizeTask(id), 3100);
    }, 100);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => {
      const guessed = extType(f.name);
      queueTask({
        method: "file",
        name: f.name,
        type: guessed.label,
        size: `${humanSize(f.size)}`,
        payload: { fileName: f.name },
      });
    });
  };

  const openMethodModal = (id: ImportMethodId) => {
    setActiveMethod(id);
    setMethodInput("");
    setMethodError("");
    setMethodModalOpen(true);
  };

  const confirmMethodImport = async () => {
    if (!activeMethod) return;
    const v = methodInput.trim();
    if (!v) {
      setMethodError("请输入内容");
      return;
    }
    const method = activeMethod;
    const methodLabel = importMethods.find((m) => m.id === method)?.label || "导入";
    if (method === "arxiv") {
      const arxivId = parseArxivId(v);
      if (!arxivId) {
        setMethodError("请输入有效的 arXiv 链接或 ID（例如：2403.01234 或 https://arxiv.org/abs/2403.01234）");
        return;
      }
      const absUrl = `https://arxiv.org/abs/${arxivId}`;
      const pdfUrl = `https://arxiv.org/pdf/${arxivId}.pdf`;
      const meta = await tryFetchArxivMeta(arxivId);
      queueTask({
        method,
        name: meta?.title?.trim() ? meta.title.trim() : v,
        type: "ARXIV",
        size: `${methodLabel}`,
        payload: {
          ref: v,
          arxivId,
          absUrl,
          pdfUrl,
          title: meta?.title,
          abstract: meta?.abstract,
          authors: meta?.authors,
          published: meta?.published,
        },
      });
      setMethodModalOpen(false);
      return;
    }
    queueTask({
      method,
      name: v,
      type: methodLabel.replace("导入", "").toUpperCase(),
      size: `${methodLabel}`,
      payload: { ref: v },
    });
    setMethodModalOpen(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-transparent">
        <div className="border-b border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center justify-between gap-4 px-5 md:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/knowledge")}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                知识库
              </button>
              <span className="text-muted-foreground">/</span>
              <span className="text-sm font-semibold">导入内容</span>
            </div>
            <div className="flex items-center gap-2">
              <button className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent">
                <Upload className="h-4 w-4" />
                批量导入
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                <Sparkles className="h-4 w-4" />
                新建知识集
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1100px] space-y-5">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">导入内容</h2>
              <p className="mt-2 text-sm text-muted-foreground">将文献、文档、对话、链接与系统成果统一沉淀到知识库</p>
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-4">
                <div
                  className={`rounded-3xl border border-dashed p-8 text-center shadow-sm transition-colors ${
                    dragging ? "border-primary bg-primary/5" : "border-border bg-card"
                  }`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFiles(e.dataTransfer.files);
                  }}
                >
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-muted/35 text-foreground">
                    <UploadCloud className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <div className="text-base font-medium text-foreground">
                    拖拽文件到此处，或 <span className="text-primary">点击选择文件</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">支持 PDF、DOCX、图片、Markdown 等格式，单文件最大 200MB</div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                  >
                    <Upload className="h-4 w-4" />选择文件
                  </button>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                </div>

                <div className="rounded-3xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="mb-4 text-sm font-semibold">解析与存储设置</h3>
                  <div className="space-y-3">
                    {settings.map((s, i) => (
                      <div key={s.label} className="flex items-start gap-3 rounded-2xl border border-border bg-muted/15 p-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{s.label}</div>
                          <div className="text-xs text-muted-foreground">{s.desc}</div>
                          {s.label === "加入知识集" && s.enabled && (
                            <div className="mt-2">
                              <select
                                value={targetKbId}
                                onChange={(e) => setTargetKbId(e.target.value)}
                                className="h-9 w-full rounded-lg border border-border bg-background px-3 text-xs font-medium text-foreground outline-none"
                              >
                                {bases.map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {s.label === "加入知识集" && !s.enabled && (
                            <p className="mt-2 text-xs text-muted-foreground">导入内容将暂存为任务记录，不会写入知识库。</p>
                          )}
                        </div>
                        <button
                          onClick={() => setSettings((prev) => prev.map((item, idx) => (idx === i ? { ...item, enabled: !item.enabled } : item)))}
                          className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${s.enabled ? "bg-primary" : "bg-muted"}`}
                        >
                          <span className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-all ${s.enabled ? "left-6" : "left-1"}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {importMethods.map((m) => {
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.id}
                          onClick={() => openMethodModal(m.id as ImportMethodId)}
                          className="rounded-2xl border border-border bg-background p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                        >
                          <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${m.bg} ${m.color}`}><Icon className="h-5 w-5" /></div>
                          <div className="text-sm font-semibold">{m.label}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{m.desc}</div>
                          <div className="mt-3 text-xs font-medium text-primary">去导入</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold">导入任务（最近）</h3>
                    <button className="text-sm text-primary">查看全部</button>
                  </div>
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <div key={task.name} className="flex items-start gap-3 rounded-2xl border border-white/70 bg-[#FAFAFC] p-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-xs font-semibold">{task.type}</div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{task.name}</div>
                          <div className="text-xs text-muted-foreground">{task.size}</div>
                          {task.status === "parsing" && (
                            <div className="mt-2 h-1.5 rounded-full bg-muted">
                              <div className="h-1.5 rounded-full bg-foreground" style={{ width: `${task.progress ?? 0}%` }} />
                            </div>
                          )}
                          {task.status === "done" && task.createdItemIds?.length ? (
                            <button
                              onClick={() => navigate(`/knowledge/${task.kbId}`)}
                              className="mt-2 text-xs font-medium text-primary hover:underline"
                            >
                              已入库，查看
                            </button>
                          ) : null}
                        </div>
                        <div className="text-right text-xs text-muted-foreground">{task.time}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <div className="mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">支持的导入格式与来源</h3></div>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div><span className="font-medium text-foreground">文献与文件：</span> PDF、DOCX、Markdown、图片</div>
                    <div><span className="font-medium text-foreground">参考文献：</span> DOI、RIS、BibTeX、arXiv</div>
                    <div><span className="font-medium text-foreground">其他来源：</span> 对话、链接、成果入库</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center text-xs text-muted-foreground">所有导入内容仅对你可见，安全加密存储，支持随时删除。</div>
          </div>
        </div>
      </main>
      {showCreate && <CreateKBModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
      {methodModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setMethodModalOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-semibold">
                  {importMethods.find((m) => m.id === activeMethod)?.label || "导入"}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  输入标识或链接，创建导入任务并自动入库。
                </div>
              </div>
              <button onClick={() => setMethodModalOpen(false)} className="rounded-lg px-2 py-1 text-sm text-muted-foreground hover:bg-accent">关闭</button>
            </div>

            <div className="mt-5 space-y-3">
              <input
                autoFocus
                value={methodInput}
                onChange={(e) => {
                  setMethodInput(e.target.value);
                  setMethodError("");
                }}
                placeholder={activeMethod === "doi" ? "例如：10.1038/s41586-021-03821-3" : activeMethod === "arxiv" ? "例如：2403.01234" : activeMethod === "social" ? "例如：https://..." : "请输入内容"}
                className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
              {methodError ? <div className="text-xs text-destructive">{methodError}</div> : null}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setMethodModalOpen(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent">
                取消
              </button>
              <button
                onClick={() => void confirmMethodImport()}
                className="rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  !methodInput.trim() ||
                  (activeMethod === "arxiv" ? !parseArxivId(methodInput.trim()) : false)
                }
              >
                开始导入
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
